import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2, Heart, CheckCircle2, AlertTriangle, FileText } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

/**
 * Teen Charter Sign (Magic Link) — Sprint 195
 * ───────────────────────────────────────────
 * Lands the teen here from the parent's "I just signed" email/SMS.
 *
 *  URL: /teen-charter-sign?token=…
 *  Flow:
 *    1. GET /api/family/charter-invite/preview → render charter terms
 *    2. Teen taps "Sign & start"
 *    3. POST /api/family/charter-invite/sign → backend marks teen signed,
 *       flips charter to active, issues an auth token, redirects to /.
 */

const PURPLE = '#7B4FD8';
const ORANGE = '#F5831F';

const TeenCharterSign = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = searchParams.get('token');
  const [phase, setPhase] = useState('loading');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!token) {
      setPhase('invalid');
      setError('This link is missing its token.');
      return;
    }
    let cancelled = false;
    apiClient.request(`/api/family/charter-invite/preview?token=${encodeURIComponent(token)}`)
      .then((res) => {
        if (cancelled) return;
        setData(res);
        if (res.teen_signed) setPhase('already-signed');
        else if (!res.parent_signed) setPhase('parent-not-yet');
        else setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Invitation link no longer valid.');
        setPhase('invalid');
      });
    return () => { cancelled = true; };
  }, [token]);

  const sign = async () => {
    setPhase('signing');
    setError(null);
    try {
      const res = await apiClient.request('/api/family/charter-invite/sign', {
        method: 'POST',
        body: JSON.stringify({ token, signature_mark: data?.teen_name || '' }),
      });
      if (login && res.access_token) {
        await login({ access_token: res.access_token, user: { user_id: res.user_id, name: res.name } });
      } else if (res.access_token) {
        localStorage.setItem('hibff_auth_token', res.access_token);
      }
      setPhase('done');
      setTimeout(() => navigate(res.next_step || '/'), 1500);
    } catch (err) {
      setError(err?.message || 'Could not sign the charter.');
      setPhase('ready');
    }
  };

  const teenFirst = data?.teen_name?.split(' ')[0] || 'You';
  const parentFirst = data?.parent_name?.split(' ')[0] || 'Your parent';

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-orange-50 py-12 px-4">
      <div className="max-w-xl mx-auto bg-white rounded-3xl shadow-xl p-6 sm:p-10" data-testid="teen-charter-sign-page">
        <img src="/hibff-logo.png" alt="HiBFF" className="h-12 mx-auto mb-6" />

        {phase === 'loading' && (
          <div className="text-center py-12">
            <Loader2 size={40} className="animate-spin mx-auto mb-3" style={{ color: PURPLE }} />
            <p className="text-gray-600 text-sm">Loading your Charter…</p>
          </div>
        )}

        {phase === 'invalid' && (
          <Result icon={<AlertTriangle size={28} style={{ color: '#B42318' }} />} iconBg="#FEF3F2" heading="This link can't be used">{error || 'Ask your parent to resend the Charter invite.'}</Result>
        )}

        {phase === 'parent-not-yet' && (
          <Result icon={<AlertTriangle size={28} style={{ color: '#B54708' }} />} iconBg="#FFFAEB" heading="One second">{parentFirst} hasn&apos;t signed yet. Check back in a few minutes.</Result>
        )}

        {phase === 'already-signed' && (
          <Result icon={<CheckCircle2 size={28} style={{ color: '#059669' }} />} iconBg="#ECFDF5" heading="Already signed">You&apos;ve already signed your Charter. Open the HiBFF app to continue.</Result>
        )}

        {phase === 'done' && (
          <Result icon={<CheckCircle2 size={28} style={{ color: '#059669' }} />} iconBg="#ECFDF5" heading="You&apos;re in.">Signing complete. Taking you to your dashboard…</Result>
        )}

        {(phase === 'ready' || phase === 'signing') && data && (
          <div data-testid="teen-charter-sign-form">
            <div
              className="rounded-2xl p-5 sm:p-6 mb-5 text-white relative overflow-hidden"
              style={{ background: `linear-gradient(135deg, ${PURPLE} 0%, ${ORANGE} 100%)` }}
            >
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/15 mb-3 text-[11px] uppercase tracking-wider font-bold">
                <FileText size={12} /> Your Charter
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold mb-1.5">
                {parentFirst} signed. Your turn, {teenFirst}.
              </h1>
              <p className="text-white/90 text-sm sm:text-base">
                Read it through, then tap sign. Takes about 60 seconds.
              </p>
            </div>

            {/* Charter terms (renders parent_signed metadata + commitments) */}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-5 mb-5 max-h-80 overflow-y-auto" data-testid="teen-charter-terms">
              <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                {data.terms || (
                  <>
                    We agree to use HiBFF together, openly and honestly.
                    {'\n\n'}• I&apos;ll show up — at least 10 minutes a few times a week.
                    {'\n'}• I&apos;ll be honest about my mood, my wins, and my mistakes.
                    {'\n'}• {parentFirst} will respect my privacy with my AvA conversations.
                    {'\n'}• If anything feels wrong, either of us can pause anytime.
                    {'\n\n'}This is a shared agreement. We&apos;re a team.
                  </>
                )}
              </p>
              <p className="text-xs text-gray-500 mt-4 italic">
                Signed by {data.parent_name} on {data.parent_signed_at ? new Date(data.parent_signed_at).toLocaleDateString() : 'today'}.
              </p>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{ background: '#FEF3F2', color: '#B42318', border: '1px solid #FDA29B' }}>{error}</div>
            )}

            <button
              onClick={sign}
              disabled={phase === 'signing'}
              className="w-full px-6 py-4 rounded-full font-bold text-white text-base disabled:opacity-60 transition-transform hover:scale-[1.01] active:scale-[0.99]"
              style={{ background: `linear-gradient(135deg, ${ORANGE}, #FF9F4A)`, boxShadow: `0 4px 20px ${ORANGE}55` }}
              data-testid="teen-charter-sign-btn"
            >
              {phase === 'signing'
                ? <><Loader2 size={16} className="inline animate-spin mr-2" /> Signing…</>
                : <><Heart size={16} className="inline mr-2" fill="#fff" stroke="#fff" /> Sign &amp; start my journey</>}
            </button>
            <p className="text-[11px] text-gray-500 text-center mt-3">
              By signing you agree to the HiBFF charter terms above with {parentFirst}.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Result = ({ icon, iconBg, heading, children }) => (
  <div className="text-center py-10">
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ background: iconBg }}>
      {icon}
    </div>
    <h1 className="text-xl font-bold text-gray-900 mb-2">{heading}</h1>
    <p className="text-gray-600 max-w-sm mx-auto text-sm">{children}</p>
  </div>
);

export default TeenCharterSign;
